import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Sparkles, Send, Loader2, Search, ShieldCheck, Calculator, Globe, MessageSquare,
  Plus, Trash2, Settings, KeyRound, ExternalLink, Copy, Bot, User as UserIcon,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import {
  getAIAranSettings, saveAIAranSettings, deleteAIAranSettings,
  getAIAranTemplates, sendAIAranChat, listAIAranSessions,
  getAIAranSession, deleteAIAranSession,
} from '../lib/api';

const ICONS = { Search, ShieldCheck, Calculator, Globe, MessageSquare };

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  const Icon = isUser ? UserIcon : Bot;
  return (
    <div
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
      data-testid={`ai-msg-${msg.role}`}
    >
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser ? 'bg-primary/20 text-primary' : 'bg-amber-500/20 text-amber-400'
      }`}>
        <Icon size={16} />
      </div>
      <div className={`max-w-[85%] rounded-lg px-4 py-3 ${
        isUser ? 'bg-primary/10 border border-primary/20' : 'bg-secondary/50 border border-border'
      }`}>
        {isUser ? (
          <p className="text-sm whitespace-pre-wrap break-words text-foreground">{msg.content}</p>
        ) : (
          <div className="prose prose-sm prose-invert max-w-none break-words [&_p]:my-2 [&_h1]:text-base [&_h2]:text-base [&_h3]:text-sm [&_ul]:my-2 [&_ol]:my-2 [&_code]:text-amber-400 [&_code]:bg-zinc-900 [&_code]:px-1 [&_code]:rounded">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsDialog({ open, onClose, settings, onSaved }) {
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(settings?.model || 'gpt-5-mini');
  const [useEmergent, setUseEmergent] = useState(settings?.use_emergent_key || false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setApiKey('');
      setModel(settings?.model || 'gpt-5-mini');
      setUseEmergent(settings?.use_emergent_key || false);
    }
  }, [open, settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAIAranSettings({
        api_key: apiKey || null,
        model,
        use_emergent_key: useEmergent,
        provider: 'openai',
      });
      toast.success('Configuración guardada');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('¿Eliminar la configuración? La IA dejará de funcionar hasta que reconfigures.')) return;
    setSaving(true);
    try {
      await deleteAIAranSettings();
      toast.success('Configuración eliminada');
      onSaved();
      onClose();
    } catch (e) {
      toast.error('Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="ai-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={18} /> Configuración de IA Arancel
          </DialogTitle>
          <DialogDescription>
            Conecta tu cuenta de OpenAI para usar tu propia IA. Tú pagas el consumo directo a OpenAI (no se descuentan créditos Emergent).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300">
            <p className="font-medium mb-1">¿Cómo obtener tu API key?</p>
            <ol className="list-decimal list-inside space-y-1 text-amber-200/80">
              <li>Ve a <a className="underline inline-flex items-center gap-1" href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com/api-keys <ExternalLink size={11} /></a></li>
              <li>Crea una cuenta (separada de ChatGPT Plus) y agrega saldo en Billing.</li>
              <li>Click <em>"Create new secret key"</em>, cópiala y pégala abajo. Empieza con <code className="bg-amber-500/10 px-1 rounded">sk-</code>.</li>
            </ol>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Modo de operación</label>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-sm">Usar mi propia API key de OpenAI</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {useEmergent ? 'Activa esto y pega tu key abajo' : 'Recomendado — pagas directo a OpenAI'}
                </p>
              </div>
              <Switch
                checked={!useEmergent}
                onCheckedChange={(v) => setUseEmergent(!v)}
                data-testid="ai-use-own-key-switch"
              />
            </div>
            {useEmergent && settings?.emergent_available && (
              <p className="text-xs text-muted-foreground mt-2">
                Modo Emergent activo: el consumo se descuenta de tus créditos universales.
              </p>
            )}
          </div>

          {!useEmergent && (
            <div>
              <label className="text-sm font-medium mb-1 block">OpenAI API key</label>
              <Input
                type="password"
                placeholder={settings?.configured ? `Actual: ${settings.masked_key}` : 'sk-…'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                data-testid="ai-api-key-input"
              />
              {settings?.configured && (
                <p className="text-xs text-muted-foreground mt-1">Deja vacío para conservar la key actual</p>
              )}
            </div>
          )}

          <div>
            <label className="text-sm font-medium mb-1 block">Modelo</label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(settings?.allowed_models || ['gpt-5-mini', 'gpt-5.1']).map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              <strong>gpt-5-mini</strong>: rápido, ~$0.30 / 1M tokens · <strong>gpt-5.1</strong>: más preciso, ~$2.50 / 1M tokens
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {settings?.configured && (
            <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="ai-delete-config-btn">
              <Trash2 size={14} className="mr-2" /> Eliminar
            </Button>
          )}
          <div className="flex gap-2 sm:ml-auto">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="ai-save-config-btn">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
              Guardar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AIArancelPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [sending, setSending] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const messagesEndRef = useRef(null);

  const loadSettings = useCallback(async () => {
    try {
      const r = await getAIAranSettings();
      setSettings(r.data);
      // Auto-open settings if not configured and user is admin
      if (isAdmin && !r.data.configured && !r.data.use_emergent_key) {
        setShowSettings(true);
      }
    } catch {
      /* noop */
    }
  }, [isAdmin]);

  const loadSessions = useCallback(async () => {
    try {
      const r = await listAIAranSessions();
      setSessions(r.data);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadSessions();
    getAIAranTemplates().then((r) => setTemplates(r.data)).catch(() => {});
  }, [loadSettings, loadSessions]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openSession = async (sid) => {
    setActiveSessionId(sid);
    try {
      const r = await getAIAranSession(sid);
      setMessages(r.data || []);
    } catch {
      setMessages([]);
    }
  };

  const newSession = () => {
    setActiveSessionId(null);
    setMessages([]);
    setInput('');
    setActiveTemplate(null);
  };

  const handleDeleteSession = async (sid, e) => {
    e?.stopPropagation();
    if (!confirm('¿Eliminar esta conversación?')) return;
    try {
      await deleteAIAranSession(sid);
      if (sid === activeSessionId) newSession();
      loadSessions();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const userMsg = { role: 'user', content: input, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    const messageToSend = input;
    const tplId = activeTemplate?.id;
    setInput('');
    setSending(true);
    try {
      const r = await sendAIAranChat({
        session_id: activeSessionId,
        message: messageToSend,
        template_id: tplId,
      });
      setActiveSessionId(r.data.session_id);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: r.data.answer,
        model: r.data.model,
        created_at: new Date().toISOString(),
      }]);
      loadSessions();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Error al enviar mensaje';
      toast.error(detail);
      // Surface the error inside the chat so the user has context
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `**⚠️ Error**\n\n${detail}`,
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
      setActiveTemplate(null);
    }
  };

  const handleTemplateClick = (tpl) => {
    setActiveTemplate(tpl);
    setInput(tpl.placeholder.split('\n')[1]?.replace('Ejemplo: ', '') || '');
  };

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copiado al portapapeles');
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-[calc(100vh-100px)] gap-4" data-testid="ai-arancel-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Sparkles className="text-amber-400" size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-heading text-foreground">IA Arancel</h1>
              <p className="text-xs text-muted-foreground">
                Asistente experto en clasificación arancelaria mexicana (TIGIE / SAT)
                {settings?.configured && !settings?.use_emergent_key && (
                  <span className="ml-2 text-emerald-400">· Tu API · {settings.model}</span>
                )}
                {settings?.use_emergent_key && (
                  <span className="ml-2 text-amber-400">· Modo Emergent · {settings.model}</span>
                )}
              </p>
            </div>
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettings(true)}
              data-testid="ai-open-settings-btn"
            >
              <Settings size={14} className="mr-2" /> Configurar
            </Button>
          )}
        </div>

        {/* Body: sessions sidebar + chat */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 min-h-0">
          {/* Sessions sidebar */}
          <div className="hidden lg:flex flex-col gap-2 bg-card border border-border rounded-lg p-3 overflow-y-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={newSession}
              className="justify-start"
              data-testid="ai-new-session-btn"
            >
              <Plus size={14} className="mr-2" /> Nueva conversación
            </Button>
            <div className="text-xs text-muted-foreground uppercase font-medium pt-2">
              Recientes ({sessions.length})
            </div>
            <div className="flex-1 space-y-1 overflow-y-auto">
              {sessions.length === 0 ? (
                <p className="text-xs text-muted-foreground p-2">Sin conversaciones aún</p>
              ) : sessions.map((s) => (
                <div
                  key={s.session_id}
                  onClick={() => openSession(s.session_id)}
                  className={`group flex items-center justify-between gap-1 px-2 py-2 rounded-md cursor-pointer transition-colors ${
                    activeSessionId === s.session_id ? 'bg-primary/10 text-primary' : 'hover:bg-secondary/50'
                  }`}
                  data-testid={`ai-session-${s.session_id}`}
                >
                  <p className="text-xs truncate flex-1">{s.title}</p>
                  <button
                    onClick={(e) => handleDeleteSession(s.session_id, e)}
                    className="opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Chat area */}
          <div className="flex flex-col bg-card border border-border rounded-lg overflow-hidden min-h-0">
            {/* Quick templates (only when no messages) */}
            {messages.length === 0 && (
              <div className="p-6 flex-1 overflow-y-auto">
                <div className="max-w-2xl mx-auto">
                  <h2 className="text-lg font-medium text-foreground mb-1">¿En qué te ayudo hoy?</h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    Selecciona una acción rápida o escribe tu pregunta directamente.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {templates.map((tpl) => {
                      const Icon = ICONS[tpl.icon] || MessageSquare;
                      return (
                        <button
                          key={tpl.id}
                          onClick={() => handleTemplateClick(tpl)}
                          className={`p-4 rounded-lg border text-left transition-all hover:border-primary/50 hover:bg-primary/5 ${
                            activeTemplate?.id === tpl.id ? 'border-primary bg-primary/10' : 'border-border'
                          }`}
                          data-testid={`ai-template-${tpl.id}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Icon size={16} className="text-amber-400" />
                            <p className="font-medium text-sm">{tpl.title}</p>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-line">
                            {tpl.placeholder.split('\n')[0]}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.length > 0 && (
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ai-messages-list">
                {messages.map((m, i) => (
                  <div key={i} className="group">
                    <MessageBubble msg={m} />
                    {m.role === 'assistant' && (
                      <div className="flex justify-start ml-11 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => copyMessage(m.content)}
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                        >
                          <Copy size={11} /> Copiar
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {sending && (
                  <div className="flex gap-3" data-testid="ai-loading">
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400">
                      <Bot size={16} />
                    </div>
                    <div className="bg-secondary/50 border border-border rounded-lg px-4 py-3">
                      <Loader2 size={16} className="animate-spin text-amber-400" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}

            {/* Input bar */}
            <div className="border-t border-border p-3 bg-secondary/20">
              {activeTemplate && (
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full">
                    {activeTemplate.title}
                  </span>
                  <button onClick={() => setActiveTemplate(null)} className="text-muted-foreground hover:text-foreground">
                    × quitar
                  </button>
                </div>
              )}
              <div className="flex gap-2 items-end">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={activeTemplate?.placeholder?.split('\n')[0] || 'Escribe tu pregunta…  (Enter para enviar, Shift+Enter para salto de línea)'}
                  rows={2}
                  className="resize-none min-h-[60px] max-h-[200px]"
                  disabled={sending}
                  data-testid="ai-chat-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={sending || !input.trim()}
                  size="icon"
                  className="h-10 w-10 flex-shrink-0"
                  data-testid="ai-send-btn"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2 text-center">
                Las sugerencias son orientativas. Valida siempre antes del despacho oficial.
              </p>
            </div>
          </div>
        </div>
      </div>

      <SettingsDialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        onSaved={loadSettings}
      />
    </DashboardLayout>
  );
}
